// ─── Collage Editor V1.2 ──────────────────────────────────────
// Property inspector for collage elements.
// V1.2: preset picker, save-as-preset, drag-to-upload, duplicate, add/remove.

import React, { useState, useCallback } from 'react';
import {
  ChevronDown, ChevronUp, Image as ImageIcon, Type, FileText,
  Heading, Trash2, RotateCw, Move, Layers, EyeOff, Copy, Bookmark,
  ArrowUp, ArrowDown, GripVertical, Play
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getElementEditorLabel } from '../../lib/utils/collageIntent';
import PresetPicker from './PresetPicker';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  image_card: { icon: ImageIcon, label: 'Image', color: 'bg-blue-500' },
  text_note: { icon: FileText, label: 'Note', color: 'bg-amber-500' },
  text_block: { icon: Type, label: 'Text', color: 'bg-emerald-500' },
  title_block: { icon: Heading, label: 'Title', color: 'bg-violet-500' },
  slideshow_element: { icon: Play, label: 'Slideshow', color: 'bg-cyan-500' },
};

const SECTION_LABELS = {
  'hero-collage': 'Hero Section',
  'story-collage': 'Story Section',
  'footer-collage': 'Footer Section',
};

const NEW_ELEMENT_DEFAULTS = {
  image_card: {
    type: 'image_card', style: 'plain-photo', src: '', caption: '', rotation: 0,
    intent: { role: 'image', importance: 'medium', behavior: 'flexible', mobileBehavior: 'preserve', overlapPolicy: 'avoidText', animationPriority: 3 },
    layout: { desktop: { x: 0.5, y: 0.5, w: 0.3, z: 5 }, mobile: { x: 0.5, y: 0.5, w: 0.6, z: 5 } },
  },
  text_note: {
    type: 'text_note', style: 'handwritten-paper', content: 'Your note here...', rotation: 0,
    intent: { role: 'decorative', importance: 'medium', behavior: 'flexible', mobileBehavior: 'preserve', overlapPolicy: 'allow', animationPriority: 4 },
    layout: { desktop: { x: 0.5, y: 0.5, w: 0.22, z: 6 }, mobile: { x: 0.5, y: 0.5, w: 0.42, z: 6 } },
  },
  text_block: {
    type: 'text_block', style: 'typewriter', content: 'Your text here...',
    intent: { role: 'bodyText', importance: 'medium', behavior: 'flexible', mobileBehavior: 'preserve', overlapPolicy: 'avoidImportant', animationPriority: 3 },
    layout: { desktop: { x: 0.5, y: 0.5, w: 0.28, z: 4 }, mobile: { x: 0.5, y: 0.5, w: 0.62, z: 4 } },
  },
  title_block: {
    type: 'title_block', style: 'hero-title', title: 'Your Title', subtitle: '', meta: '', buttonText: '', buttonHref: '',
    intent: { role: 'primaryHeadline', importance: 'high', behavior: 'fixed', mobileBehavior: 'preserve', overlapPolicy: 'none', animationPriority: 1 },
    layout: { desktop: { x: 0.5, y: 0.5, w: 0.6, z: 4 }, mobile: { x: 0.5, y: 0.5, w: 0.84, z: 4 } },
  },
};

// ─── Slider Row ───────────────────────────────────────────────
function SliderRow({ label, value, onChange, min = 0, max = 1, step = 0.01, suffix = '' }) {
  const display = suffix === '%' ? `${Math.round(value * 100)}%` : value;
  return (
    <div className="flex items-center gap-3">
      <span className="text-[11px] text-gray-500 dark:text-gray-400 w-6 shrink-0">{label}</span>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        className="flex-1 h-1 appearance-none bg-gray-200 dark:bg-gray-700 rounded-full outline-none accent-blue-500 cursor-pointer"
        data-testid={`slider-${label.toLowerCase()}`}
      />
      <span className="text-[11px] text-gray-600 dark:text-gray-300 w-10 text-right font-mono tabular-nums">{display}</span>
    </div>
  );
}

// ─── Element Editor Card ──────────────────────────────────────
function ElementEditorCard({
  element, sectionId, overrides, onUpdate, onImageUpload,
  uploadingField, layoutMode, isSelected, onSelect, onDuplicate, onRemove, onSavePreset,
}) {
  const [open, setOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const meta = TYPE_META[element.type] || TYPE_META.text_block;
  const Icon = meta.icon;

  const merged = { ...element, ...overrides };
  const layoutKey = layoutMode;
  const mergedLayout = {
    ...(element.layout?.[layoutKey] || {}),
    ...(overrides?.layout?.[layoutKey] || {}),
  };

  const updateField = (field, val) => {
    onUpdate(sectionId, element.id, { ...overrides, [field]: val });
  };

  const updateLayout = (prop, val) => {
    const currentOverrides = overrides?.layout || {};
    const currentLayoutMode = currentOverrides[layoutKey] || {};
    onUpdate(sectionId, element.id, {
      ...overrides,
      layout: { ...currentOverrides, [layoutKey]: { ...currentLayoutMode, [prop]: val } },
    });
  };

  // Drag-to-upload handlers
  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type.startsWith('image/')) {
      onImageUpload(sectionId, element.id, 'src', file);
    }
  };

  const elName = getElementEditorLabel(element);
  const hiddenOnMobile = element.intent?.mobileBehavior === 'hide';
  const importanceBadge = element.intent?.importance;
  const isCustom = element.id.startsWith('custom-');

  return (
    <div
      className={`border rounded-lg overflow-hidden transition-all ${
        isSelected ? 'border-blue-500 ring-1 ring-blue-500/30 bg-blue-50/50 dark:bg-blue-900/10' :
        hiddenOnMobile && layoutMode === 'mobile'
          ? 'border-gray-200/50 dark:border-gray-800/50 opacity-50 bg-white dark:bg-gray-900'
          : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900'
      }`}
      data-testid={`collage-element-${element.id}`}
      onClick={() => onSelect(sectionId, element.id)}
    >
      {/* Header */}
      <div className="flex items-center">
        <button
          onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
          className="flex-1 flex items-center gap-2.5 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
          data-testid={`toggle-element-${element.id}`}
        >
          <div className={`w-5 h-5 rounded flex items-center justify-center ${meta.color}`}>
            <Icon className="w-3 h-3 text-white" />
          </div>
          <span className="text-xs font-medium text-gray-800 dark:text-gray-200 flex-1 text-left truncate">
            {elName}
          </span>
          {hiddenOnMobile && layoutMode === 'mobile' && (
            <span className="flex items-center gap-0.5 text-[9px] text-orange-500 bg-orange-50 dark:bg-orange-900/20 px-1.5 py-0.5 rounded-full">
              <EyeOff className="w-2.5 h-2.5" /> hidden
            </span>
          )}
          {importanceBadge && (
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
              importanceBadge === 'high' ? 'bg-red-50 text-red-500 dark:bg-red-900/20' :
              importanceBadge === 'medium' ? 'bg-yellow-50 text-yellow-600 dark:bg-yellow-900/20' :
              'bg-gray-50 text-gray-400 dark:bg-gray-800'
            }`}>{importanceBadge}</span>
          )}
          {open ? <ChevronUp className="w-3.5 h-3.5 text-gray-400" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}
        </button>

        {/* Action buttons */}
        <div className="flex items-center gap-0.5 pr-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              const currentZ = mergedLayout.z ?? 1;
              updateLayout('z', Math.min(20, currentZ + 1));
            }}
            className="p-1 rounded text-gray-400 hover:text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
            title="Bring Forward"
            data-testid={`z-up-${element.id}`}
          >
            <ArrowUp className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              const currentZ = mergedLayout.z ?? 1;
              updateLayout('z', Math.max(1, currentZ - 1));
            }}
            className="p-1 rounded text-gray-400 hover:text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
            title="Send Backward"
            data-testid={`z-down-${element.id}`}
          >
            <ArrowDown className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onSavePreset(element, overrides); }}
            className="p-1 rounded text-gray-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
            title="Save as Preset"
            data-testid={`save-preset-${element.id}`}
          >
            <Bookmark className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDuplicate(sectionId, element); }}
            className="p-1 rounded text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            title="Duplicate"
            data-testid={`duplicate-${element.id}`}
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
          {isCustom && (
            <button
              onClick={(e) => { e.stopPropagation(); onRemove(sectionId, element.id); }}
              className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              title="Remove"
              data-testid={`remove-${element.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-4 border-t border-gray-100 dark:border-gray-800">
          {/* ─── Image Card ─── */}
          {element.type === 'image_card' && (
            <div className="space-y-3">
              <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400">Photo</label>
              {/* Drop zone */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`relative rounded-xl border-2 border-dashed p-4 transition-all text-center ${
                  dragOver
                    ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                    : merged.src
                      ? 'border-gray-200 dark:border-gray-700'
                      : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800'
                }`}
                data-testid={`dropzone-${element.id}`}
              >
                {merged.src ? (
                  <div className="relative group">
                    <img
                      src={merged.src.startsWith('/') ? `${API}${merged.src}` : merged.src}
                      alt=""
                      className="w-full aspect-square object-cover rounded-lg"
                    />
                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 rounded-lg transition-opacity flex items-center justify-center gap-2">
                      <label className="px-3 py-1.5 rounded-lg bg-white text-xs font-medium cursor-pointer text-gray-800 hover:bg-gray-100">
                        Replace
                        <input type="file" accept="image/*" className="hidden"
                          onChange={e => { const f = e.target.files?.[0]; if (f) onImageUpload(sectionId, element.id, 'src', f); e.target.value = ''; }}
                        />
                      </label>
                      <button onClick={() => updateField('src', '')}
                        className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-medium hover:bg-red-600">
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="py-6">
                    <ImageIcon className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      {dragOver ? 'Drop image here' : 'Drag & drop an image'}
                    </p>
                    <label className={`inline-block px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer ${
                      uploadingField === `${sectionId}.${element.id}.src`
                        ? 'bg-gray-100 text-gray-400'
                        : 'bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400'
                    }`}>
                      {uploadingField === `${sectionId}.${element.id}.src` ? 'Uploading...' : 'Or browse files'}
                      <input type="file" accept="image/*" className="hidden"
                        disabled={uploadingField === `${sectionId}.${element.id}.src`}
                        onChange={e => { const f = e.target.files?.[0]; if (f) onImageUpload(sectionId, element.id, 'src', f); e.target.value = ''; }}
                        data-testid={`upload-${element.id}`}
                      />
                    </label>
                  </div>
                )}
              </div>
              {element.caption !== undefined && (
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Caption</label>
                  <input type="text"
                    className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-1 focus:ring-blue-500"
                    value={merged.caption || ''} onChange={e => updateField('caption', e.target.value)}
                    placeholder="Caption text..." data-testid={`caption-${element.id}`}
                  />
                </div>
              )}
            </div>
          )}

          {/* ─── Text Note ─── */}
          {element.type === 'text_note' && (
            <div>
              <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Content</label>
              <textarea rows={3}
                className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                value={merged.content || ''} onChange={e => updateField('content', e.target.value)}
                placeholder="Handwritten note..." data-testid={`content-${element.id}`}
              />
            </div>
          )}

          {/* ─── Text Block ─── */}
          {element.type === 'text_block' && (
            <div>
              <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Content</label>
              <textarea rows={3}
                className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                value={merged.content || ''} onChange={e => updateField('content', e.target.value)}
                placeholder="Text content..." data-testid={`content-${element.id}`}
              />
            </div>
          )}

          {/* ─── Title Block ─── */}
          {element.type === 'title_block' && (
            <div className="space-y-2.5">
              <div>
                <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Title</label>
                <input type="text"
                  className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-1 focus:ring-blue-500"
                  value={merged.title || ''} onChange={e => updateField('title', e.target.value)}
                  placeholder="Title..." data-testid={`title-${element.id}`}
                />
              </div>
              {element.subtitle !== undefined && (
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Subtitle</label>
                  <textarea rows={2}
                    className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                    value={merged.subtitle || ''} onChange={e => updateField('subtitle', e.target.value)}
                    placeholder="Subtitle..." data-testid={`subtitle-${element.id}`}
                  />
                </div>
              )}
              {element.meta !== undefined && (
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Meta</label>
                  <input type="text"
                    className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-1 focus:ring-blue-500"
                    value={merged.meta || ''} onChange={e => updateField('meta', e.target.value)}
                    placeholder="Date · Location" data-testid={`meta-${element.id}`}
                  />
                </div>
              )}
              {element.buttonText !== undefined && (
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Button Text</label>
                  <input type="text"
                    className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-1 focus:ring-blue-500"
                    value={merged.buttonText || ''} onChange={e => updateField('buttonText', e.target.value)}
                    placeholder="Button label..." data-testid={`button-${element.id}`}
                  />
                </div>
              )}
            </div>
          )}

          {/* ─── Rotation ─── */}
          {element.rotation !== undefined && (
            <div>
              <label className="flex items-center gap-1.5 text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                <RotateCw className="w-3 h-3" /> Rotation
              </label>
              <SliderRow label="" value={merged.rotation || 0} onChange={v => updateField('rotation', v)} min={-15} max={15} step={0.5} suffix="" />
              <div className="text-right text-[10px] text-gray-400 font-mono mt-0.5">{merged.rotation || 0}&deg;</div>
            </div>
          )}

          {/* ─── Layout controls ─── */}
          <div>
            <label className="flex items-center gap-1.5 text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-2">
              <Move className="w-3 h-3" /> Position &amp; Size
            </label>
            <div className="space-y-1.5">
              <SliderRow label="X" value={mergedLayout.x ?? 0.5} onChange={v => updateLayout('x', v)} suffix="%" />
              <SliderRow label="Y" value={mergedLayout.y ?? 0.5} onChange={v => updateLayout('y', v)} suffix="%" />
              <SliderRow label="W" value={mergedLayout.w ?? 0.3} onChange={v => updateLayout('w', v)} min={0.05} max={0.95} suffix="%" />
              <SliderRow label="Z" value={mergedLayout.z ?? 1} onChange={v => updateLayout('z', Math.round(v))} min={1} max={20} step={1} suffix="" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Section Group ────────────────────────────────────────────
function CollageSectionGroup({
  section, content, onUpdate, onImageUpload, uploadingField,
  layoutMode, onToggle, selectedElement, onSelect, onDuplicate, onRemove, onAdd, onSavePreset, token,
}) {
  const [open, setOpen] = useState(true);
  const elements = content[section.id]?._elements || section.elements || [];
  const sectionContent = content[section.id] || {};
  const label = SECTION_LABELS[section.id] || section.id;

  return (
    <div className="space-y-2" data-testid={`collage-section-group-${section.id}`}>
      <div className="flex items-center justify-between">
        <button
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-200 hover:text-gray-600 dark:hover:text-gray-400 transition-colors"
        >
          <Layers className="w-3.5 h-3.5 text-gray-400" />
          {label}
          <span className="text-[10px] font-normal text-gray-400">({elements.length})</span>
          {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
        <button
          onClick={() => onToggle(section.id)}
          className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
            section.visible ? 'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400' : 'bg-gray-100 text-gray-400 dark:bg-gray-800'
          }`}
          data-testid={`toggle-section-${section.id}`}
        >
          {section.visible ? 'ON' : 'OFF'}
        </button>
      </div>

      {open && section.visible && (
        <div className="space-y-2 pl-1">
          {elements.map(element => (
            <ElementEditorCard
              key={element.id}
              element={element}
              sectionId={section.id}
              overrides={sectionContent[element.id] || {}}
              onUpdate={onUpdate}
              onImageUpload={onImageUpload}
              uploadingField={uploadingField}
              layoutMode={layoutMode}
              isSelected={selectedElement?.sectionId === section.id && selectedElement?.elementId === element.id}
              onSelect={onSelect}
              onDuplicate={onDuplicate}
              onRemove={onRemove}
              onSavePreset={onSavePreset}
            />
          ))}
          <PresetPicker sectionId={section.id} onAdd={onAdd} token={token} />
        </div>
      )}
    </div>
  );
}

// ─── Main Collage Editor ──────────────────────────────────────
export default function CollageEditor({
  sections, content, onUpdate, onToggle, onImageUpload,
  uploadingField, selectedElement, onSelect, onDuplicate, onRemove, onAdd, onSavePreset, onAutoBalance, token,
}) {
  const [layoutMode, setLayoutMode] = useState('mobile');

  const handleUpdate = useCallback((sectionId, elementId, overrides) => {
    onUpdate(sectionId, elementId, overrides);
  }, [onUpdate]);

  return (
    <div className="space-y-5" data-testid="collage-editor">
      {/* Layout mode toggle */}
      <div className="p-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-2">
          Editing Layout
        </label>
        <div className="flex gap-1 p-0.5 rounded-lg bg-gray-100 dark:bg-gray-800">
          <button
            onClick={() => setLayoutMode('mobile')}
            className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-all ${
              layoutMode === 'mobile' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
            }`}
            data-testid="layout-mode-mobile"
          >Mobile</button>
          <button
            onClick={() => setLayoutMode('desktop')}
            className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-all ${
              layoutMode === 'desktop' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
            }`}
            data-testid="layout-mode-desktop"
          >Desktop</button>
        </div>
        {onAutoBalance && (
          <button
            onClick={() => onAutoBalance(layoutMode)}
            className="mt-2 w-full py-1.5 rounded-lg text-[11px] font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 border border-amber-200 dark:border-amber-800 transition-colors"
            data-testid="auto-balance-btn"
          >
            Auto-balance
          </button>
        )}
      </div>

      {/* Section groups */}
      {sections.map(section => (
        <CollageSectionGroup
          key={section.id}
          section={section}
          content={content}
          onUpdate={handleUpdate}
          onImageUpload={onImageUpload}
          uploadingField={uploadingField}
          layoutMode={layoutMode}
          onToggle={onToggle}
          selectedElement={selectedElement}
          onSelect={onSelect}
          onDuplicate={onDuplicate}
          onRemove={onRemove}
          onAdd={onAdd}
          onSavePreset={onSavePreset}
          token={token}
        />
      ))}
    </div>
  );
}
